import { TASK_STATUS_LABELS, type TaskSource, type TaskStatus } from '@netcon-work/shared'
import { useState } from 'react'
import { AufgabenVerweis } from '../AufgabeKontext'
import { useStatusSetzen } from '../../lib/mutationen'
import type { Aufgabe } from '../../lib/queries'
import type { VorhabenSpur as Spur } from './lage'
import { Button, SectionTitle, SourceBadge } from '../ui/primitives'

/**
 * **Zone 2 – die Hauptspur** ([Z-02](../../../../../docs/phasen/zusatz-02-mein-tag.md)).
 *
 * Der größte Mangel der alten Ansicht: Vorhaben waren eine Menüseite weiter links, Aufgaben
 * standen flach untereinander und trugen ein GitLab-Kürzel. Wer an zwei Vorhaben gleichzeitig
 * arbeitete, sah das nirgends.
 *
 * Hier ist das Vorhaben der **Behälter**, und jede Karte zeigt drei Dinge, die es vorher nicht
 * gab:
 *
 * - **Fortschritt statt Anzahl** – „7 von 11" plus Balken. Man sieht, wie weit es noch ist.
 * - **Bewegung statt Datum** – „bewegt sich · heute" oder „steht · 6 Tage". Die ehrlichste
 *   Kennzahl für ein Vorhaben, und sie erzeugt Zug ohne Alarmfarbe ([F-62](../../../../../docs/OFFENE-FRAGEN.md)).
 * - **Pausiert ist ein Zustand** – gestrichelt, nicht verschwunden. Man weiß, dass es existiert,
 *   ohne sich schuldig zu fühlen.
 */
export function VorhabenSpuren({
  spuren,
  ohneVorhaben,
  vorhabenOeffnen,
}: {
  spuren: readonly Spur[]
  ohneVorhaben: readonly Aufgabe[]
  vorhabenOeffnen: (id: string) => void
}) {
  /**
   * **Ohne Vorhaben gibt es die Spur nicht** (Nils, 25.08.2026: *„Das «Meine Vorhaben» ist ja
   * eigentlich immer leer. Kann es dann nicht weg?"*).
   *
   * ## Die Messung gibt ihm recht
   *
   * Am 25.08.2026 hingen **7 von 67** offenen Aufgaben an einem Vorhaben – fünf Vorhaben
   * hatten genau eine, zwei hatten keine. Für fast jeden im Team war die „Hauptspur" also eine
   * Überschrift über einer einzigen Karte: *„Ohne Vorhaben – steht in «Als Nächstes» mit
   * drin."* Ein Kasten, der auf einen anderen Kasten verweist.
   *
   * ## Warum die Spur trotzdem bleibt und nicht gelöscht wird
   *
   * Weil die Absicht aus [Z-02](../../../../../docs/phasen/zusatz-02-mein-tag.md) richtig ist:
   * morgens denkt niemand in Zuständen, sondern *„ich schiebe zwei Vorhaben nach vorn"*. Nur
   * gibt es diese zwei Vorhaben in den Daten noch nicht. Die Spur **verdient sich ihren Platz**
   * – sobald jemand an einem Vorhaben arbeitet, steht sie wieder da, ohne dass jemand etwas
   * einschalten muss.
   *
   * ## Und warum „Ohne Vorhaben" allein nicht reicht
   *
   * Die Karte sagt wörtlich, dass ihr Inhalt rechts nochmal steht. Sie war als **Fach**
   * gedacht, damit die Spur für Ticket-Arbeiter nicht lügt – aber ein Fach, das nur neben
   * leeren Fächern steht, ist kein Fach, sondern eine Wiederholung. Seit
   * [Z-08](../../../../../docs/phasen/zusatz-08-jetzt-in-spalten.md) steht dieselbe Trennung
   * ohnehin eine Zone höher, in Spalten und mit Fortschritt.
   */
  if (spuren.length === 0) return null

  return (
    <section>
      <SectionTitle hint="Hauptspur – woran du gerade arbeitest">Meine Vorhaben</SectionTitle>
      {spuren.map((s) => (
        <Karte key={s.id} spur={s} oeffnen={() => vorhabenOeffnen(s.id)} />
      ))}
      {/**
       * **„Ohne Vorhaben" ist ein Fach, kein Rest.** Bei NetCon-Tickets hat die Mehrheit kein
       * Vorhaben; ohne diese Karte wäre die Hauptspur für jeden gelogen, der überwiegend
       * Tickets macht.
       */}
      {ohneVorhaben.length > 0 && <OhneVorhabenKarte aufgaben={ohneVorhaben} />}
    </section>
  )
}

function Karte({ spur, oeffnen }: { spur: Spur; oeffnen: () => void }) {
  /**
   * **Zugeklappt seit [Z-06](../../../../../docs/phasen/zusatz-06-nehmen-und-starten.md).**
   *
   * Vorher stand die Aufgabenliste jedes laufenden Vorhabens offen da – und dieselbe Zeile stand
   * damit zweimal auf dem Bildschirm: links unter ihrem Vorhaben, rechts in der Rangfolge. Ein
   * Test hat es gefangen („Found multiple elements"), derselbe Fehler wie schon bei Z-02.5.
   *
   * Die Trennlinie ist jetzt die **Absicht**, nicht der Besitz: Zone 2 beantwortet *„was schiebe
   * ich voran"* – dafür reichen Fortschritt und Bewegung. *„Was mache ich jetzt"* beantwortet
   * „Als Nächstes", einmal, für Eigenes und Vorrat zusammen. Die Zeilen sind nicht weg, sie
   * liegen einen Klick tiefer.
   */
  const [offen, setOffen] = useState(false)

  if (spur.pausiert) {
    return (
      <article className="mb-[9px] rounded-card border border-dashed border-line2 px-[15px] py-[13px] opacity-70">
        <div className="flex items-start gap-2.5">
          <div className="min-w-0 flex-1">
            <h4 className="text-[15px] font-semibold">
              <button type="button" onClick={oeffnen} className="cursor-pointer hover:underline">
                {spur.label}
              </button>
            </h4>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted">
              <span className="rounded-md border border-line bg-page px-[7px] py-[2px] text-[10.5px]">
                Pausiert
              </span>
              {spur.wiederAb && <span>Zeitfenster bis {spur.wiederAb}</span>}
              <span className="tabular">{spur.meine.length} offen für dich</span>
            </div>
          </div>
          <Kuerzel namen={spur.mitwirkende} />
        </div>
      </article>
    )
  }

  return (
    <article className="mb-[9px] rounded-card border border-line2 bg-surface px-[15px] py-[13px] shadow-task">
      <div className="flex items-start gap-2.5">
        <div className="min-w-0 flex-1">
          <h4 className="text-[15px] font-semibold tracking-[-0.15px]">
            <button
              type="button"
              onClick={oeffnen}
              className="cursor-pointer text-left hover:text-accent hover:underline"
            >
              {spur.label}
            </button>
          </h4>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted">
            <span className="rounded-md bg-vorhaben-bg px-[7px] py-[2px] text-[10.5px] font-semibold text-pool-projekte">
              Vorhaben
            </span>
            <span className="tabular">
              {spur.stand.erledigt} von {spur.stand.gesamt} erledigt
            </span>
            <Bewegung spur={spur} />
          </div>
        </div>
        <Kuerzel namen={spur.mitwirkende} />
        <button
          type="button"
          onClick={() => setOffen((o) => !o)}
          aria-expanded={offen}
          aria-label={offen ? 'Aufgaben ausblenden' : 'Aufgaben zeigen'}
          className="cursor-pointer px-1.5 py-0.5 text-[11px] text-muted hover:text-ink"
        >
          {offen ? '▲' : '▼'}
        </button>
      </div>

      <div className="mt-2.5 h-1 overflow-hidden rounded-sm bg-line">
        <i
          className="block h-full rounded-sm bg-pool-projekte transition-[width] duration-500"
          style={{ width: `${spur.prozent}%` }}
        />
      </div>

      {offen && (
        <div className="mt-2.5 border-t border-line pt-0.5">
          {spur.meine.map((a, i) => (
            <Zeile key={a.id} aufgabe={a} dran={i === 0} />
          ))}
        </div>
      )}
    </article>
  )
}

/**
 * **„Ohne Vorhaben" ist eine Auskunft, keine Wand** ([Z-06](../../../../../docs/phasen/zusatz-06-nehmen-und-starten.md)).
 *
 * Bis zum 24.08.2026 stand hier die vollständige Liste – bei Christian **16 Zeilen**,
 * aufgeklappt, ohne Sortierung, direkt neben dem Vorrat. Genau das ist die „Backlog-Wand", die
 * Konzept Kap. 5.1 ausschließt, und der Anlass für Nils' Frage, wann man denn nun zieht und wann
 * man auf den eigenen Vorrat zugreift.
 *
 * Was als Nächstes dran ist, beantwortet jetzt die Rangfolge rechts – **einmal**, für Eigenes
 * und Vorrat zusammen. Hier bleibt die Zahl und der Weg dorthin: zugeklappt, aufklappbar,
 * dieselben Zeilen wie vorher.
 */
function OhneVorhabenKarte({ aufgaben }: { aufgaben: readonly Aufgabe[] }) {
  /**
   * **Zugeklappt, nicht aufgeklappt** – der einzige inhaltliche Unterschied zur alten Karte,
   * und der ganze Punkt: die Antwort auf „was mache ich jetzt" steht nicht mehr hier.
   */
  const [offen, setOffen] = useState(false)
  const laufend = aufgaben.filter((a) => a.status === 'in_arbeit').length

  return (
    <article className="mb-[9px] rounded-card border border-line2 bg-surface px-[15px] py-[13px] shadow-task">
      <div className="flex items-start gap-2.5">
        <div className="min-w-0 flex-1">
          <h4 className="text-[15px] font-semibold tracking-[-0.15px]">Ohne Vorhaben</h4>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted">
            <span className="rounded-md border border-line bg-page px-[7px] py-[2px] text-[10.5px]">
              Einzelaufgaben
            </span>
            <span className="tabular">{aufgaben.length} offen für dich</span>
            {laufend > 0 && <span className="tabular">{laufend} in Arbeit</span>}
            <span>steht in „Als Nächstes" mit drin</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOffen((o) => !o)}
          aria-expanded={offen}
          aria-label={offen ? 'Aufgaben ausblenden' : 'Aufgaben zeigen'}
          className="cursor-pointer px-1.5 py-0.5 text-[11px] text-muted hover:text-ink"
        >
          {offen ? '▲' : '▼'}
        </button>
      </div>
      {offen && (
        <div className="mt-2.5 border-t border-line pt-0.5">
          {aufgaben.map((a) => (
            <Zeile key={a.id} aufgabe={a} dran={false} />
          ))}
        </div>
      )}
    </article>
  )
}

function Zeile({ aufgabe, dran }: { aufgabe: Aufgabe; dran: boolean }) {
  const status = useStatusSetzen()
  const laeuft = aufgabe.status === 'in_arbeit'

  return (
    <div className="group flex items-center gap-2.5 border-b border-line py-[7px] text-[13px] last:border-b-0">
      <span
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${
          dran ? 'bg-accent shadow-[0_0_0_3px_var(--color-accent-soft)]' : 'bg-line2'
        }`}
        aria-hidden="true"
      />
      <AufgabenVerweis
        aufgabeId={aufgabe.id}
        className="min-w-0 flex-1 cursor-pointer truncate text-left hover:text-accent hover:underline"
        title={aufgabe.title}
      >
        {aufgabe.title}
      </AufgabenVerweis>
      <SourceBadge source={aufgabe.sourceId as TaskSource} label={aufgabe.externalKey} />
      {/*
        Ein Knopf, nicht fünf: von hier aus fängt man an. Alles Weitere entscheidet sich
        auf der Karte in „Jetzt" oder im Detail.
      */}
      {laeuft ? (
        <span className="shrink-0 text-[11px] text-muted">{TASK_STATUS_LABELS.in_arbeit}</span>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          disabled={status.isPending}
          onClick={() => status.mutate({ id: aufgabe.id, status: 'in_arbeit' as TaskStatus })}
          aria-label={`${aufgabe.title} starten`}
          className="opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
        >
          Starten
        </Button>
      )}
    </div>
  )
}

/** Höchstens vier Kürzel – danach eine Zahl. Fünf Kreise sind kein Team, sondern ein Muster. */
function Kuerzel({ namen }: { namen: readonly string[] }) {
  if (namen.length === 0) return null
  const sichtbar = namen.slice(0, 4)
  return (
    <span className="flex shrink-0" title={namen.join(', ')}>
      {sichtbar.map((n, i) => (
        <span
          key={n}
          className={`grid h-[21px] w-[21px] place-items-center rounded-full border-2 border-surface bg-vorhaben-bg text-[8.5px] font-bold text-pool-projekte ${
            i > 0 ? '-ml-1.5' : ''
          }`}
        >
          {initialen(n)}
        </span>
      ))}
      {namen.length > sichtbar.length && (
        <span className="ml-1 self-center text-[10.5px] text-muted">
          +{namen.length - sichtbar.length}
        </span>
      )}
    </span>
  )
}

function initialen(name: string): string {
  return name
    .split(' ')
    .filter((w) => /^[A-ZÄÖÜ]/.test(w))
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
}

function Bewegung({ spur }: { spur: Spur }) {
  if (spur.zustand === 'unbekannt') {
    return <span title="Zu diesem Vorhaben ist noch nichts passiert.">noch keine Bewegung</span>
  }
  if (spur.zustand === 'steht') {
    return (
      <span
        className="font-semibold text-warn"
        title="Seit Tagen ist an keiner Aufgabe dieses Vorhabens etwas passiert."
      >
        steht · {spur.bewegtSeitTagen} Tage ohne Bewegung
      </span>
    )
  }
  if (spur.zustand === 'bewegt') {
    return (
      <span className="font-semibold text-good-text">
        bewegt sich · {spur.bewegtSeitTagen === 0 ? 'heute' : 'gestern'}
      </span>
    )
  }
  return <span>zuletzt vor {spur.bewegtSeitTagen} Tagen</span>
}
